import React from "react";
import ReactDOM from "react-dom/client";
import {
  MantineProvider,
  ColorSchemeScript,
  createTheme,
  rem,
  MantineColorsTuple,
  Button,
  Card,
  Paper,
  Anchor,
  TextInput,
  Textarea,
  Select,
  Badge,
  Alert,
  Notification,
} from "@mantine/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { App } from "./App";
import { ErrorBoundary } from "./ErrorBoundary";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";

const oceanumPrimaryTuple: MantineColorsTuple = [
  "#eefcfd", "#d3f4fa", "#ace8f5", "#73d7ed", "#32bbde",
  "#179ec3", "#167fa4", "#186686", "#1d546d", "#1c465d",
];
const oceanumSecondaryTuple: MantineColorsTuple = [
  "#fef3f1", "#fde5e0", "#fccdc5", "#f9a99a", "#f57b67",
  "#e9553a", "#B94628", "#9b3b22", "#7e321f", "#692e1f",
];

const colors = {
  oceanumPrimary: {
    50: "#eefcfd", 100: "#d3f4fa", 200: "#ace8f5", 300: "#73d7ed",
    400: "#32bbde", 500: "#179ec3", 600: "#167fa4", 700: "#186686",
    800: "#1d546d", 900: "#1c465d", 950: "#0d2e40",
  },
};

// Dark-mode palette built from official oceanumPrimary shades (dark end)
const oceanumDark: MantineColorsTuple = [
  colors.oceanumPrimary[200],  // 0 – primary text on dark  (#ace8f5)
  colors.oceanumPrimary[300],  // 1 – secondary text        (#73d7ed)
  colors.oceanumPrimary[400],  // 2 – dimmed                (#32bbde)
  colors.oceanumPrimary[500],  // 3 – muted                 (#179ec3)
  colors.oceanumPrimary[600],  // 4 – borders               (#167fa4)
  colors.oceanumPrimary[700],  // 5 – hover surfaces        (#186686)
  colors.oceanumPrimary[800],  // 6 – card / input surfaces (#1d546d)
  colors.oceanumPrimary[950],  // 7 – main panel background (#0d2e40)
  "#071d2c",                   // 8 – deeper dark
  "#040f17",                   // 9 – deepest
];

const theme = createTheme({
  colors: {
    oceanumPrimary: oceanumPrimaryTuple,
    oceanumSecondary: oceanumSecondaryTuple,
    dark: oceanumDark,
  },
  primaryColor: "oceanumPrimary",
  primaryShade: { light: 9, dark: 5 },
  shadows: {
    xs: "0 1px 2px rgba(0, 0, 0, 0.05)",
    sm: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
    md: "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",
    lg: "0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)",
    xl: "0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)",
  },
  fontFamily: "Roboto, sans-serif",
  headings: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: "700",
    sizes: {
      h1: { fontSize: rem(36), lineHeight: "1.2" },
      h2: { fontSize: rem(30), lineHeight: "1.25" },
      h3: { fontSize: rem(24), lineHeight: "1.3" },
      h4: { fontSize: rem(20), lineHeight: "1.35" },
      h5: { fontSize: rem(18), lineHeight: "1.4" },
      h6: { fontSize: rem(16), lineHeight: "1.4" },
    },
  },
  radius: {
    xs: rem(2), sm: rem(4), md: rem(8), lg: rem(12), xl: rem(16),
  },
  defaultRadius: "md",
  focusRing: "auto",
  cursorType: "pointer",
  autoContrast: true,
  components: {
    Button: Button.extend({ defaultProps: { radius: "xl" } }),
    Card: Card.extend({ defaultProps: { shadow: "sm", padding: "lg", radius: "md", withBorder: true } }),
    Paper: Paper.extend({ defaultProps: { shadow: "xs", radius: "md" } }),
    Anchor: Anchor.extend({ defaultProps: { underline: "hover" } }),
    TextInput: TextInput.extend({ defaultProps: { radius: "md" } }),
    Textarea: Textarea.extend({ defaultProps: { radius: "md" } }),
    Select: Select.extend({ defaultProps: { radius: "md" } }),
    Badge: Badge.extend({ defaultProps: { radius: "sm" } }),
    Alert: Alert.extend({ defaultProps: { radius: "md" } }),
    Notification: Notification.extend({ defaultProps: { radius: "md" } }),
  },
});

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ColorSchemeScript defaultColorScheme="light" />
    <ErrorBoundary>
      <MantineProvider theme={theme} defaultColorScheme="light">
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </MantineProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
